// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Minimum allowed interval between trades in seconds.
 * Used by both API validation and CMS UI.
 */
export const MIN_INTERVAL_SECONDS = 30;

export const AUTOMATIC_STREAK_CYCLE_SKIP_REASON = {
  SPREAD_THRESHOLD: "SPREAD_THRESHOLD",
  VOLUME_COOLDOWN: "VOLUME_COOLDOWN",
  TRADE_FAILED: "TRADE_FAILED",
  TIME_DEVIATION: "TIME_DEVIATION",
  CONCURRENT_EXECUTION: "CONCURRENT_EXECUTION",
} as const;
export type AutomaticStreakCycleSkipReason =
  (typeof AUTOMATIC_STREAK_CYCLE_SKIP_REASON)[keyof typeof AUTOMATIC_STREAK_CYCLE_SKIP_REASON];

// Streak status enum
export const AUTOMATIC_STREAK_STATUS = {
  PENDING: "PENDING",
  IN_PROGRESS: "IN_PROGRESS",
  COMPLETED: "COMPLETED",
  STOPPED: "STOPPED",
  CANCELLED: "CANCELLED",
} as const;
export type AUTOMATIC_STREAK_STATUS = (typeof AUTOMATIC_STREAK_STATUS)[keyof typeof AUTOMATIC_STREAK_STATUS];
export type AutomaticStreakStatus = (typeof AUTOMATIC_STREAK_STATUS)[keyof typeof AUTOMATIC_STREAK_STATUS];

export const AUTOMATIC_STREAK_ACTIVE_STATUSES = [
  AUTOMATIC_STREAK_STATUS.PENDING,
  AUTOMATIC_STREAK_STATUS.IN_PROGRESS,
] as const;

// Stop reason enum
export const AUTOMATIC_STREAK_STOP_REASON = {
  USER_CANCELLED: "USER_CANCELLED",
  EXCHANGE_ACCOUNT_DISCONNECTED: "EXCHANGE_ACCOUNT_DISCONNECTED",
  EPOCH_MAX_VOLUME_REACHED: "EPOCH_MAX_VOLUME_REACHED",
  USER_MAX_VOLUME_REACHED: "USER_MAX_VOLUME_REACHED",
  EPOCH_PAUSED: "EPOCH_PAUSED",
  EPOCH_FINISHED: "EPOCH_FINISHED",
  EPOCH_NOT_FOUND: "EPOCH_NOT_FOUND",
  CAMPAIGN_NOT_FOUND: "CAMPAIGN_NOT_FOUND",
  TRADE_FAILED: "TRADE_FAILED",
  EXECUTION_TIME_DEVIATION: "EXECUTION_TIME_DEVIATION",
  INSUFFICIENT_BALANCE: "INSUFFICIENT_BALANCE",
  SYSTEM_ERROR: "SYSTEM_ERROR",
  FEATURE_DISABLED: "FEATURE_DISABLED",
  AUTOPILOT_CONFIG_UPDATED: "AUTOPILOT_CONFIG_UPDATED",
  CANCELLED_BY_ADMIN: "CANCELLED_BY_ADMIN",
  // Campaign gained a non-empty `authorizedParticipants` whitelist that excludes
  // the runner. Trades placed past this point score no reward, so the streak stops.
  RESTRICTED_PARTICIPANTS: "RESTRICTED_PARTICIPANTS",
} as const;

export type AutomaticStreakStopReason =
  (typeof AUTOMATIC_STREAK_STOP_REASON)[keyof typeof AUTOMATIC_STREAK_STOP_REASON];

/**
 * Human-readable labels for stop reasons.
 * Used by both API (SSE notifications) and frontend (UI display).
 */
export const AUTOMATIC_STREAK_STOP_REASON_LABELS: Record<AutomaticStreakStopReason, string> = {
  [AUTOMATIC_STREAK_STOP_REASON.USER_CANCELLED]: "Cancelled by user",
  [AUTOMATIC_STREAK_STOP_REASON.EXCHANGE_ACCOUNT_DISCONNECTED]: "Exchange account disconnected",
  [AUTOMATIC_STREAK_STOP_REASON.EPOCH_MAX_VOLUME_REACHED]: "Campaign max volume reached",
  [AUTOMATIC_STREAK_STOP_REASON.USER_MAX_VOLUME_REACHED]: "Your max volume reached",
  [AUTOMATIC_STREAK_STOP_REASON.EPOCH_PAUSED]: "Campaign paused",
  [AUTOMATIC_STREAK_STOP_REASON.EPOCH_FINISHED]: "Campaign epoch ended",
  [AUTOMATIC_STREAK_STOP_REASON.EPOCH_NOT_FOUND]: "Campaign epoch not found",
  [AUTOMATIC_STREAK_STOP_REASON.CAMPAIGN_NOT_FOUND]: "Campaign not found",
  [AUTOMATIC_STREAK_STOP_REASON.TRADE_FAILED]: "Trade failed",
  [AUTOMATIC_STREAK_STOP_REASON.EXECUTION_TIME_DEVIATION]: "System was busy",
  [AUTOMATIC_STREAK_STOP_REASON.INSUFFICIENT_BALANCE]: "Insufficient balance",
  [AUTOMATIC_STREAK_STOP_REASON.SYSTEM_ERROR]: "System error",
  [AUTOMATIC_STREAK_STOP_REASON.FEATURE_DISABLED]: "Autopilot temporarily disabled",
  [AUTOMATIC_STREAK_STOP_REASON.AUTOPILOT_CONFIG_UPDATED]: "Autopilot configuration updated",
  [AUTOMATIC_STREAK_STOP_REASON.CANCELLED_BY_ADMIN]: "Cancelled by admin",
  [AUTOMATIC_STREAK_STOP_REASON.RESTRICTED_PARTICIPANTS]: "Campaign restricted to selected participants",
} as const;

/**
 * Get human-readable label for a stop reason.
 * Returns the raw reason if no label is found.
 */
export const getStopReasonLabel = (reason?: AutomaticStreakStopReason): string => {
  if (!reason) return "";
  return AUTOMATIC_STREAK_STOP_REASON_LABELS[reason] ?? reason;
};
